import React, { useState, useMemo } from 'react';
import { usePersonalMediaLibrary } from '../../contexts/PersonalMediaLibraryContext';
import { PersonalMediaUpload } from '../../components/PersonalMediaUpload/PersonalMediaUpload';
import { Pagination } from '../../components/Pagination';
import { usePersistentImage } from '../../hook/usePersistentImage';
import { deleteUserMedia } from '../../services/personalMediaList';
import { Media } from '../../types/Media';
import { plusIcon, searchIcon } from '../../utils/images';
import './PersonalMediaPage.scss';

const PersonalMediaItem: React.FC<{ image: Media; onDelete: (name: string) => void }> = ({
  image,
  onDelete,
}) => {
  const { imageUrl, loading, error } = usePersistentImage({
    url: image.url,
    lastModified: image.last_modified,
  });
  const [showEditPopup, setShowEditPopup] = useState(false);

  return (
    <div className="personal-media-item">
      <div className="personal-media-item__image-wrapper">
        {loading && <div className="personal-media-item__loading">Loading...</div>}
        {error && <div className="personal-media-item__error">Failed to load</div>}
        {!loading && !error && imageUrl && (
          <img src={imageUrl} alt={image.name} className="personal-media-item__image"/>
        )}
      </div>
      <div className="personal-media-item__footer">
        <span className="personal-media-item__name" title={image.name}>{image.name}</span>
        <button className="personal-media-item__edit" onClick={() => setShowEditPopup(true)}>
          Edit
        </button>
      </div>

      {showEditPopup && (
        <div className="personal-media-item__popup-overlay" onClick={() => setShowEditPopup(false)}>
          <div className="personal-media-item__popup" onClick={e => e.stopPropagation()}>
            <p className="personal-media-item__popup-name" title={image.name}>{image.name}</p>
            <button
              className="personal-media-item__popup-delete"
              onClick={() => { setShowEditPopup(false); onDelete(image.name); }}
            >
              Delete
            </button>
            <button
              className="personal-media-item__popup-cancel"
              onClick={() => setShowEditPopup(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const PersonalMediaPage: React.FC = () => {
  const { mediaData, loading, error, refresh, currentPage, totalPages, goToPage } = usePersonalMediaLibrary();
  const [showUpload, setShowUpload] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = useMemo(() =>
    mediaData.images.filter(img => img.name.toLowerCase().includes(searchQuery.toLowerCase())),
    [mediaData.images, searchQuery]
  );

  const handleDelete = async (filename: string) => {
    if (!window.confirm(`Delete "${filename}"?`)) return;
    try {
      await deleteUserMedia(filename);
      await refresh();
    } catch {
      alert('Failed to delete file.');
    }
  };

  if (loading && mediaData.images.length === 0) {
    return <div className="personal-media-page"><div>Loading your media...</div></div>;
  }

  if (error) {
    return (
      <div className="personal-media-page">
        <p>Error: {error}</p>
        <button onClick={refresh}>Retry</button>
      </div>
    );
  }

  return (
    <>
      <div className="personal-media-page">
        <div className="personal-media-page__header">
          <h1 className="personal-media-page__title">My Media</h1>
          <button className="personal-media-page__add-button" onClick={() => setShowUpload(true)}>
            <img src={plusIcon} alt="" />
            <span>Add Media</span>
          </button>
        </div>

        <div className="personal-media-page__search">
          <img src={searchIcon} alt="search" />
          <input
            type="text"
            placeholder="Search media files"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="personal-media-page__grid">
          {filtered.length > 0 ? (
            filtered.map(img => (
              <PersonalMediaItem
                key={img.url}
                image={img}
                onDelete={handleDelete}
              />
            ))
          ) : (
            <p className="personal-media-page__empty">No media files found.</p>
          )}
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={goToPage}
        />
      </div>

      {showUpload && (
        <PersonalMediaUpload
          onClose={() => setShowUpload(false)}
          onSuccess={refresh}
        />
      )}
    </>
  );
};
